import type { ScheduleTaskType } from '@risksmart-app/domain/src/types/consts';
import type { FC } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { StatusType } from 'src/components/form/controlled-select/ControlledSelect';
import { Controller } from 'src/components/form/field-controller/Controller';
import { FormField } from 'src/components/form/form/FormField';
import Multiselect from 'src/components/form/multi-select';

import type { ScheduleFormFieldData } from './scheduleFormSchema';
import { keepTargetsOfTaskTypes, pickSelectedOptions } from './scheduleTargets';

export interface TaskTypeOption {
  value: ScheduleTaskType;
  label: string;
  description?: string;
}

type Props = {
  options: TaskTypeOption[];
  statusType: StatusType;
};

export const TaskTypeField: FC<Props> = ({ options, statusType }) => {
  const { t } = useTranslation(['common']);
  const { control, getValues, setValue } =
    useFormContext<ScheduleFormFieldData>();
  const { error } = control.getFieldState('TaskTypes');

  return (
    <Controller
      name={'TaskTypes'}
      control={control}
      render={({ field: { ref, onChange, onBlur, value } }) => (
        <FormField
          label={t('scheduler.fields.task_type')}
          errorText={error?.message}
          stretch={true}
          testId={'schedule-task-types'}
        >
          <Multiselect
            ref={ref}
            options={options}
            statusType={statusType}
            selectedOptions={options.filter((option) =>
              value.includes(option.value)
            )}
            onBlur={onBlur}
            onChange={(e) => {
              const taskTypes = pickSelectedOptions(
                options,
                e.detail.selectedOptions
              ).map((option) => option.value);
              // Pruned in form state rather than filtered at read time: a
              // stale target or assignee left in the values reaches the
              // payload through any other reader that forgets the same filter.
              const pruned = keepTargetsOfTaskTypes(getValues(), taskTypes);
              setValue('Targets', pruned.Targets);
              setValue('AssigneesByTarget', pruned.AssigneesByTarget);
              onChange(taskTypes);
            }}
            placeholder={t('select')}
            empty={t('noMatchedFound')}
          />
        </FormField>
      )}
    />
  );
};
